import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "flex flex-col sm:flex-row items-center gap-5 rounded-2xl border border-gray-100 bg-white shadow-sm p-6" }
const _hoisted_2 = ["title"]
const _hoisted_3 = ["cx", "cy", "stroke"]
const _hoisted_4 = ["cx", "cy", "stroke", "stroke-dashoffset"]
const _hoisted_5 = { class: "text-2xl font-bold" }
const _hoisted_6 = { class: "flex min-w-0 flex-1 flex-col items-center sm:items-start gap-2" }
const _hoisted_7 = { class: "text-2xl font-bold text-gray-900 text-center sm:text-left" }
const _hoisted_8 = { class: "flex flex-wrap items-center justify-center sm:justify-start gap-2" }
const _hoisted_9 = { class: "flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-1 text-sm text-gray-500" }
const _hoisted_10 = {
  class: "flex items-center gap-1",
  "data-testid": "doctor-header-companies"
}
const _hoisted_11 = {
  class: "flex items-center gap-1",
  "data-testid": "doctor-header-specialties"
}
const _hoisted_12 = {
  class: "flex min-w-[6.5rem] shrink-0 flex-col items-center gap-0.5 text-center rounded-xl border border-gray-100 bg-gray-50 px-5 py-3",
  "data-testid": "doctor-header-nps"
}
const _hoisted_13 = /*#__PURE__*/_createElementVNode("p", { class: "text-[11px] font-medium uppercase tracking-wide text-gray-500" }, "NPS", -1)
const _hoisted_14 = { class: "text-xs text-gray-500" }

import { computed } from 'vue'
import { mdiOfficeBuildingOutline, mdiStethoscope } from '@mdi/js'

import SVGIcon from '@/components/SVGIcon.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'
import type { DoctorDetailResponse } from '@/Network/Types/Responses/CompanyResponses'
import { classifyNpsScore, NEUTRAL_RING_COLOR } from '@/utils/nps'
import {
  getCrmStatusConfig,
  getInitials,
  resolveAvatarUrl,
  toTitleCase
} from '../../helpers/doctorStatus'

const AVATAR_SIZE = 80
const STROKE_WIDTH = 4

export default /*#__PURE__*/_defineComponent({
  __name: 'DoctorHeader',
  props: {
    doctor: {}
  },
  setup(__props: any) {

const props = __props;



const doctorName = computed(() =>
  toTitleCase(props.doctor.doctor.user?.name || props.doctor.doctor.mainCrm.name)
)

const avatarSrc = computed(() => resolveAvatarUrl(props.doctor.doctor.user?.avatarUrl))

const initials = computed(() => getInitials(doctorName.value))

const radius = AVATAR_SIZE / 2 + STROKE_WIDTH / 2
const svgSize = radius * 2 + STROKE_WIDTH
const circumference = 2 * Math.PI * radius

const npsScore = computed(() => props.doctor.doctor.npsScore ?? null)
const npsClassification = computed(() =>
  npsScore.value === null ? null : classifyNpsScore(npsScore.value)
)
const npsTitle = computed(() =>
  npsClassification.value ? `NPS ${npsScore.value} (${npsClassification.value.label})` : 'Sem NPS'
)

const clampedScore = computed(() => {
  const score = npsScore.value
  if (score == null) return 0
  return Math.min(100, Math.max(0, (score + 100) / 2))
})

const offset = computed(() => circumference * (1 - clampedScore.value / 100))

const arcColor = computed(() => npsClassification.value?.color ?? NEUTRAL_RING_COLOR)

const crmStatus = computed(() => getCrmStatusConfig(props.doctor.doctor.mainCrm.status))
const crmLabel = computed(() => {
  const { number, uf } = props.doctor.doctor.mainCrm
  return crmStatus.value ? `CRM ${number}-${uf} · ${crmStatus.value.label}` : `CRM ${number}-${uf}`
})

const companiesLabel = computed(() => {
  const count = props.doctor.companies.length
  return count === 1 ? '1 empresa vinculada' : `${count} empresas vinculadas`
})

const specialtiesLabel = computed(() => {
  const count = props.doctor.doctor.mainCrm.doctorSpecialties?.length ?? 0
  return count === 1 ? '1 especialidade' : `${count} especialidades`
})

return (_ctx: any,_cache: any) => {
  const _component_v_avatar = _resolveComponent("v-avatar")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "relative shrink-0",
      style: _normalizeStyle({ width: svgSize + 'px', height: svgSize + 'px' }),
      title: npsTitle.value
    }, [
      (_openBlock(), _createElementBlock("svg", {
        width: svgSize,
        height: svgSize,
        class: "absolute top-0 left-0 -rotate-90",
        "aria-hidden": "true"
      }, [
        _createElementVNode("circle", {
          cx: svgSize / 2,
          cy: svgSize / 2,
          r: radius,
          stroke: _unref(NEUTRAL_RING_COLOR),
          "stroke-width": STROKE_WIDTH,
          fill: "none"
        }, null, 8, _hoisted_3),
        (clampedScore.value > 0)
          ? (_openBlock(), _createElementBlock("circle", {
              key: 0,
              cx: svgSize / 2,
              cy: svgSize / 2,
              r: radius,
              stroke: arcColor.value,
              "stroke-width": STROKE_WIDTH,
              fill: "none",
              "stroke-dasharray": circumference,
              "stroke-dashoffset": offset.value,
              "stroke-linecap": "round"
            }, null, 8, _hoisted_4))
          : _createCommentVNode("", true)
      ])),
      _createVNode(_component_v_avatar, {
        color: "primary",
        size: AVATAR_SIZE,
        class: "absolute",
        style: _normalizeStyle({ top: STROKE_WIDTH + 'px', left: STROKE_WIDTH + 'px' })
      }, {
        default: _withCtx(() => [
          _createVNode(ProtectedAvatar, {
            src: avatarSrc.value,
            size: AVATAR_SIZE
          }, {
            default: _withCtx(() => [
              _createElementVNode("span", _hoisted_5, _toDisplayString(initials.value), 1)
            ]),
            _: 1
          }, 8, ["src"])
        ]),
        _: 1
      }, 8, ["style"])
    ], 12, _hoisted_2),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("h1", _hoisted_7, _toDisplayString(doctorName.value), 1),
      _createElementVNode("div", _hoisted_8, [
        _createVNode(LabelBadge, {
          "data-testid": "doctor-header-crm",
          label: crmLabel.value,
          color: crmStatus.value?.color ?? 'blue',
          icon: null,
          size: "small"
        }, null, 8, ["label", "color"])
      ]),
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("span", _hoisted_10, [
          _createVNode(SVGIcon, { path: _unref(mdiOfficeBuildingOutline) }, null, 8, ["path"]),
          _createTextVNode(" " + _toDisplayString(companiesLabel.value), 1)
        ]),
        _createElementVNode("span", _hoisted_11, [
          _createVNode(SVGIcon, { path: _unref(mdiStethoscope) }, null, 8, ["path"]),
          _createTextVNode(" " + _toDisplayString(specialtiesLabel.value), 1)
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_12, [
      _hoisted_13,
      _createElementVNode("p", {
        class: "text-2xl font-bold leading-8",
        style: _normalizeStyle({ color: npsClassification.value?.color })
      }, _toDisplayString(npsScore.value ?? '—'), 5),
      _createElementVNode("p", _hoisted_14, _toDisplayString(npsClassification.value?.label ?? 'Sem avaliações'), 1)
    ])
  ]))
}
}

})